import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Save, RotateCcw, Info } from "lucide-react";
import toast from "react-hot-toast";
import {
  card,
  sectionTitle,
  accentBarSection,
  bodySecondary,
  microLabel,
  motionFadeUp,
  motionStaggerChild,
} from "../../../../../lib/design-system";
import { cn } from "../../../../../lib/utils";
import {
  getAttainmentConfig,
  saveAttainmentConfig,
  seedAttainmentConfigDefaults,
} from "../../../../../services/program.service";

const NBA_DEFAULTS = {
  directThresholdPct: 60,
  markCutoffPct: 40,
  directWeight: 0.8,
  indirectWeight: 0.2,
  levelBrackets: [
    { min: 0,  max: 59.9, level: 0 },
    { min: 60, max: 69.9, level: 1 },
    { min: 70, max: 79.9, level: 2 },
    { min: 80, max: 100,  level: 3 },
  ],
};

const Tooltip = ({ text }) => (
  <span className="group relative ml-1 inline-flex items-center cursor-help">
    <Info className="h-3.5 w-3.5 text-gray-400" />
    <span className="pointer-events-none absolute left-6 top-0 z-20 w-56 rounded-sm bg-gray-900 dark:bg-gray-700 text-white text-xs px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
      {text}
    </span>
  </span>
);

const NumberInput = ({ value, onChange, min, max, step = 1, className = "" }) => (
  <input
    type="number"
    min={min}
    max={max}
    step={step}
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
    className={cn(
      "w-24 px-3 py-1.5 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-center focus:outline-none focus:border-accent1 transition-colors",
      className
    )}
  />
);

const AttainmentSettingsTab = ({ programId }) => {
  const [config, setConfig]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const data = await getAttainmentConfig(programId);
      setConfig(data);
      setIsDefault(!!data._isDefault);
      setDirty(false);
    } catch {
      toast.error("Failed to load attainment config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [programId]);

  const update = (field, value) => {
    setConfig((c) => ({ ...c, [field]: value }));
    setDirty(true);
  };

  const updateBracket = (idx, field, value) => {
    setConfig((c) => ({
      ...c,
      levelBrackets: c.levelBrackets.map((b, i) =>
        i === idx ? { ...b, [field]: Number(value) } : b
      ),
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveAttainmentConfig(programId, {
        directThresholdPct: config.directThresholdPct,
        markCutoffPct:      config.markCutoffPct,
        directWeight:       config.directWeight,
        indirectWeight:     config.indirectWeight,
        levelBrackets:      config.levelBrackets,
      });
      setConfig(saved);
      setIsDefault(false);
      setDirty(false);
      toast.success("Attainment config saved");
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm("Reset all attainment parameters to NBA Tier-2 defaults?")) return;
    setSaving(true);
    try {
      const result = await seedAttainmentConfigDefaults(programId);
      setConfig(result.config);
      setIsDefault(false);
      setDirty(false);
      toast.success("Reset to NBA defaults");
    } catch (err) {
      toast.error(err.response?.data?.error || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
        ))}
      </div>
    );
  }

  if (!config) return null;

  const directPct   = Math.round(config.directWeight * 100);
  const indirectPct = Math.round(config.indirectWeight * 100);
  const weightSum   = Math.round((config.directWeight + config.indirectWeight) * 100);

  return (
    <motion.div {...motionFadeUp} className="space-y-8">

      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className={cn(sectionTitle, "text-xl sm:text-2xl")}>Attainment Settings</h2>
          <div className={cn(accentBarSection, "mt-2")} />
          <p className={cn(bodySecondary, "mt-3 max-w-2xl")}>
            Configure the NBA attainment formula parameters for this program.
            These replace the hardcoded defaults in the calculation engine.
            {isDefault && (
              <span className="ml-2 px-2 py-0.5 rounded-sm bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold">
                Using NBA defaults — not yet saved for this program
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleResetDefaults}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to NBA defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className={cn(
              "inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-accent1 text-white font-semibold text-sm transition-opacity",
              saving || !dirty ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
            )}
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {/* Section 1 — Threshold parameters */}
      <motion.section {...motionStaggerChild(0, 0)} className={cn(card, "p-6")}>
        <div className="h-1 w-8 bg-accent1 mb-4" />
        <h3 className="font-black text-gray-900 dark:text-white text-base mb-1">
          Student Pass Thresholds
        </h3>
        <p className={cn(bodySecondary, "text-xs mb-6")}>
          Define what "attained" means at the student level before rolling up to CO level.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center mb-2">
              <span className={microLabel}>Mark Cut-off %</span>
              <Tooltip text="Minimum score (%) a student must get on CO-tagged questions to count as passing that CO. NBA default: 40%." />
            </div>
            <NumberInput
              value={config.markCutoffPct}
              onChange={(v) => update("markCutoffPct", v)}
              min={0} max={100}
            />
            <p className={cn(bodySecondary, "text-xs mt-2")}>
              Student scoring below this on a CO is counted as "not attained" for that CO.
            </p>
          </div>

          <div>
            <div className="flex items-center mb-2">
              <span className={microLabel}>Direct Threshold %</span>
              <Tooltip text="% of students who must pass (above mark cut-off) for a CO to be attained at the class level. NBA Tier-2 default: 60%." />
            </div>
            <NumberInput
              value={config.directThresholdPct}
              onChange={(v) => update("directThresholdPct", v)}
              min={0} max={100}
            />
            <p className={cn(bodySecondary, "text-xs mt-2")}>
              If 60+ % of students pass CO1, CO1 is attained at L1 or above.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Section 2 — Direct / Indirect weights */}
      <motion.section {...motionStaggerChild(1, 0)} className={cn(card, "p-6")}>
        <div className="h-1 w-8 bg-accent1 mb-4" />
        <h3 className="font-black text-gray-900 dark:text-white text-base mb-1">
          Direct / Indirect Attainment Weights
        </h3>
        <p className={cn(bodySecondary, "text-xs mb-6")}>
          Combined attainment = (Direct × weight) + (Indirect survey × weight). Must sum to 100%.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center mb-2">
              <span className={microLabel}>Direct Weight (%)</span>
              <Tooltip text="Weight given to assignment + exam scores. NBA default: 80%. Use 100% if surveys are not collected." />
            </div>
            <NumberInput
              value={directPct}
              onChange={(v) => {
                const d = Math.min(100, Math.max(0, v)) / 100;
                const ind = Math.round((1 - d) * 100) / 100;
                setConfig((c) => ({ ...c, directWeight: d, indirectWeight: ind }));
                setDirty(true);
              }}
              min={0} max={100}
            />
          </div>

          <div>
            <div className="flex items-center mb-2">
              <span className={microLabel}>Indirect Weight (%)</span>
              <Tooltip text="Weight given to student Likert survey ratings (1-5 per CO). NBA default: 20%. Auto-adjusts when Direct weight changes." />
            </div>
            <NumberInput
              value={indirectPct}
              onChange={(v) => {
                const ind = Math.min(100, Math.max(0, v)) / 100;
                const d   = Math.round((1 - ind) * 100) / 100;
                setConfig((c) => ({ ...c, directWeight: d, indirectWeight: ind }));
                setDirty(true);
              }}
              min={0} max={100}
            />
          </div>
        </div>

        <div className={cn(
          "mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-semibold",
          weightSum === 100
            ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400"
            : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
        )}>
          Total: {weightSum}% {weightSum === 100 ? "✓" : "— must equal 100%"}
        </div>
      </motion.section>

      {/* Section 3 — Level brackets */}
      <motion.section {...motionStaggerChild(2, 0)} className={cn(card, "p-6")}>
        <div className="h-1 w-8 bg-accent1 mb-4" />
        <h3 className="font-black text-gray-900 dark:text-white text-base mb-1">
          CO Attainment Level Brackets
        </h3>
        <p className={cn(bodySecondary, "text-xs mb-6")}>
          Maps "% of students passing CO" to an attainment level (0–3).
          NBA standard: ≥80% → L3, ≥70% → L2, ≥60% → L1, below → L0.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 pr-6 font-semibold text-gray-700 dark:text-gray-300">Level</th>
                <th className="text-center py-2 px-4 font-semibold text-gray-700 dark:text-gray-300">Min %</th>
                <th className="text-center py-2 px-4 font-semibold text-gray-700 dark:text-gray-300">Max %</th>
                <th className="text-left py-2 pl-4 font-semibold text-gray-700 dark:text-gray-300">Meaning</th>
              </tr>
            </thead>
            <tbody>
              {[...config.levelBrackets]
                .sort((a, b) => a.level - b.level)
                .map((bracket, idx) => (
                  <tr key={bracket.level} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 pr-6">
                      <span className={cn(
                        "inline-flex items-center justify-center w-8 h-8 rounded-sm text-sm font-black",
                        bracket.level === 0 ? "bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400"
                        : bracket.level === 1 ? "bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400"
                        : bracket.level === 2 ? "bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                        : "bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400"
                      )}>
                        L{bracket.level}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <NumberInput
                        value={bracket.min}
                        onChange={(v) => updateBracket(idx, "min", v)}
                        min={0} max={100} step={0.1}
                        className="w-20"
                      />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <NumberInput
                        value={bracket.max}
                        onChange={(v) => updateBracket(idx, "max", v)}
                        min={0} max={100} step={0.1}
                        className="w-20"
                      />
                    </td>
                    <td className="py-3 pl-4 text-gray-500 dark:text-gray-400 text-xs">
                      {bracket.level === 0 && "Not attained"}
                      {bracket.level === 1 && "Low attainment"}
                      {bracket.level === 2 && "Moderate attainment"}
                      {bracket.level === 3 && "High attainment"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </motion.section>

      {/* Info box */}
      <div className="flex items-start gap-3 p-4 rounded-sm border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 text-xs">
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold">How this connects to the attainment dashboard:</span>{" "}
          Changes here take effect immediately on the next attainment computation.
          The Attainment Dashboard tab always recomputes live — no cache to clear.
          Assessment component weights (EndTerm / MidTerm / CA %) are set per-course
          in the Assessment Plan section of each course.
        </div>
      </div>
    </motion.div>
  );
};

export default AttainmentSettingsTab;
